import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  pauseTrack,
  resumeTrack,
  playNextTrack,
  playPreviousTrack,
  updatePlaybackProgress,
  seekToPosition,
  sendPlaybackHeartbeatRequest
} from '../redux/audio/actions';
import './AudioPlayer.css';

const AudioPlayer = () => {
  const dispatch = useDispatch();
  const audioRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);

  // Redux state
  const {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    playbackEventId,
    currentTrackIndex,
    tourTracks
  } = useSelector(state => state.audio);

  // Handle audio element events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      dispatch(updatePlaybackProgress(audio.currentTime, audio.duration));
    };

    const handleLoadedMetadata = () => {
      dispatch(updatePlaybackProgress(0, audio.duration));
    };

    const handleEnded = () => {
      // Auto-progress to next track if in a tour
      if (tourTracks.length > 0 && currentTrackIndex < tourTracks.length - 1) {
        dispatch(playNextTrack());
      } else {
        dispatch(pauseTrack());
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [dispatch, tourTracks, currentTrackIndex]);

  // Play/pause sync
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    if (isPlaying) {
      audio.play().catch(err => console.error('Playback error:', err));
    } else {
      audio.pause();
    }
  }, [isPlaying, currentTrack]);

  // Update audio source when track changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    // Set new source (would be presigned S3 URL from API)
    audio.src = currentTrack.audioUrl || '';
    audio.load();

    if (isPlaying) {
      audio.play().catch(err => console.error('Playback error:', err));
    }
  }, [currentTrack, isPlaying]);

  // Heartbeat interval (every 10 seconds)
  useEffect(() => {
    if (isPlaying && playbackEventId) {
      // Clear any existing interval
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }

      // Start new interval
      heartbeatIntervalRef.current = setInterval(() => {
        dispatch(sendPlaybackHeartbeatRequest(playbackEventId, Math.floor(currentTime)));
      }, 10000);

      return () => {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
        }
      };
    }
  }, [isPlaying, playbackEventId, currentTime, dispatch]);

  // Handle play/pause button
  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      dispatch(pauseTrack());
    } else {
      dispatch(resumeTrack());
    }
  }, [isPlaying, dispatch]);

  // Handle previous track
  const handlePrevious = useCallback(() => {
    if (currentTime > 3) {
      // If more than 3 seconds in, restart current track
      audioRef.current.currentTime = 0;
      dispatch(seekToPosition(0));
    } else {
      // Otherwise go to previous track
      dispatch(playPreviousTrack());
    }
  }, [currentTime, dispatch]);

  // Handle next track
  const handleNext = useCallback(() => {
    dispatch(playNextTrack());
  }, [dispatch]);

  // Format time (seconds to M:SS). Returns '0:00' for the current-time position.
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format duration. Returns '--:--' when metadata has not loaded yet (duration is
  // 0, NaN, or Infinity — all valid HTMLMediaElement states before metadata is ready).
  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Seek by setting currentTime directly on the audio element.
  // This is a client-side seek on the existing playback event — do NOT dispatch
  // playTrackRequest or anything that would create a new heartbeat event.
  const handleScrub = useCallback((e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = Number(e.target.value);
    audio.currentTime = newTime;
    // Keep Redux in sync so the time display doesn't lag behind the scrubber.
    dispatch(seekToPosition(newTime));
  }, [dispatch]);

  // Don't render if no track
  if (!currentTrack) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="audio-player">
      <audio ref={audioRef} />

      <div className="player-content">
        {/* Track info */}
        <div className="track-info">
          <div className="track-title">{currentTrack.title || 'Unknown Stop'}</div>
          <div className="track-subtitle">
            {currentTrack.tourTitle || 'Unknown Tour'}
            {currentTrackIndex >= 0 && tourTracks.length > 0 && (
              <span className="track-number">
                {' '}• Stop {currentTrackIndex + 1} of {tourTracks.length}
              </span>
            )}
          </div>
        </div>

        {/* Playback controls */}
        <div className="player-controls">
          <div className="controls-buttons">
            <button
              className="control-btn"
              onClick={handlePrevious}
              disabled={currentTrackIndex === 0 && currentTime < 3}
              title="Previous stop"
            >
              ⏮
            </button>

            <button
              className="control-btn play-pause-btn"
              onClick={handlePlayPause}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>

            <button
              className="control-btn"
              onClick={handleNext}
              disabled={currentTrackIndex === tourTracks.length - 1}
              title="Next stop"
            >
              ⏭
            </button>
          </div>

          {/* Progress bar / scrubber */}
          <div className="progress-container">
            <span className="time-current">{formatTime(currentTime)}</span>
            <input
              type="range"
              className="progress-scrubber"
              min={0}
              max={duration > 0 ? duration : 0}
              step={0.1}
              value={currentTime}
              onChange={handleScrub}
              // CSS custom property drives the fill percentage via a background gradient
              style={{ '--fill-pct': `${progress}%` }}
              aria-label="Seek"
            />
            <span className="time-duration">{formatDuration(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
